/**
 * JWT permission → subdomain read/write caps (D23 · TP-G26)
 */

const PERMISSION_RE = /^([a-z_]+):(read|create|update|delete)$/;

/** InternalEntity permission subdomain → site/studio alias (D23) */
export const SUBDOMAIN_PERMISSION_ALIASES: Record<string, string> = {
  internal_entity: 'entity',
  /** M3 marketing → appeal consolidation — legacy JWT read caps (S8 · OC3) */
  marketing_campaign: 'ephemeral_performance',
  content_asset: 'semantic_formation',
  conversion_funnel: 'external_entity',
};

function expandSubdomainAliases(subdomain: string, target: Set<string>): void {
  target.add(subdomain);
  const alias = SUBDOMAIN_PERMISSION_ALIASES[subdomain];
  if (alias) target.add(alias);
}

export function deriveSubdomainReadsFromPermissions(permissions: readonly string[]): string[] {
  const reads = new Set<string>();
  for (const perm of permissions) {
    const match = perm.match(PERMISSION_RE);
    if (!match?.[1] || match[2] !== 'read') continue;
    expandSubdomainAliases(match[1], reads);
  }
  return [...reads];
}

export function deriveSubdomainWritesFromPermissions(permissions: readonly string[]): string[] {
  const writes = new Set<string>();
  for (const perm of permissions) {
    const match = perm.match(PERMISSION_RE);
    if (!match?.[1]) continue;
    const action = match[2];
    if (action !== 'create' && action !== 'update' && action !== 'delete') continue;
    expandSubdomainAliases(match[1], writes);
  }
  return [...writes];
}

/** Read or write on a subdomain grants runtime access (D22 · D23) */
export function deriveSubdomainAccessFromPermissions(permissions: readonly string[]): string[] {
  const access = new Set<string>();
  for (const subdomain of [
    ...deriveSubdomainReadsFromPermissions(permissions),
    ...deriveSubdomainWritesFromPermissions(permissions),
  ]) {
    access.add(subdomain);
  }
  return [...access];
}

export interface SiteInternalAccessCaps {
  hasFullSiteAccess: boolean;
  canAccessStudio: boolean;
  subdomains: Set<string>;
}

import { SITE_STUDIO_PUBLISH_PERMISSION } from '@shared/site/site-studio-permissions';
const FULL_SITE_ACCESS_ROLES = new Set(['owner', 'site_publisher']);

/**
 * Internal JWT → site nav/section subdomain caps (fail-loud: no implicit owner).
 */
export function deriveSiteInternalAccessCaps(source: {
  role?: string;
  permissions?: readonly string[];
}): SiteInternalAccessCaps {
  const permissions = source.permissions ?? [];
  const role = source.role ?? '';
  const canAccessStudio =
    FULL_SITE_ACCESS_ROLES.has(role) || permissions.includes(SITE_STUDIO_PUBLISH_PERMISSION);

  if (FULL_SITE_ACCESS_ROLES.has(role) || permissions.includes('system:admin')) {
    return { hasFullSiteAccess: true, canAccessStudio: true, subdomains: new Set() };
  }

  if (permissions.includes(SITE_STUDIO_PUBLISH_PERMISSION)) {
    return { hasFullSiteAccess: true, canAccessStudio: true, subdomains: new Set() };
  }

  return {
    hasFullSiteAccess: false,
    canAccessStudio,
    subdomains: new Set(deriveSubdomainAccessFromPermissions(permissions)),
  };
}

export function subdomainMatchesAccess(subdomain: string, accessible: Set<string>): boolean {
  if (accessible.has(subdomain)) return true;
  const alias = SUBDOMAIN_PERMISSION_ALIASES[subdomain];
  if (alias && accessible.has(alias)) return true;
  for (const [from, to] of Object.entries(SUBDOMAIN_PERMISSION_ALIASES)) {
    if (subdomain === to && accessible.has(from)) return true;
  }
  return false;
}
